import React, { useMemo } from "react";
import {useNavigate} from "react-router-dom";
import {Box, SpaceBetween, Table, TableProps} from "@cloudscape-design/components";
import { Button } from "../../common/Button";
import Badge from "../../common/Badge";
import {ActivityStatus, ActivityTableProps} from "./types";
import {ActivityItem} from "../../AllActivities/types";
import { useTableSorting, createStandardComparator } from "../../../hooks";
import { getWorkflowDisplayName } from "../../../constants/workflow-tags";
import { formatTimestamp } from "../../../utils/dateTimeUtils";

const ActivityTable: React.FC<ActivityTableProps> = ({
  items = [],
  loading = false,
  showAllActions = false,
  onDownload,
  onDelete,
}) => {
  const navigate = useNavigate();

  const sortingComparator = useMemo(
    () => createStandardComparator<ActivityItem>({
      category: (item) => item.category?.toLowerCase() || "",
      reportName: (item) => item.reportName?.toLowerCase() || "",
      completedAt: (item) => new Date(item.completedAt || item.date || 0).getTime(),
      createdAt: (item) => new Date(item.createdAt || 0).getTime(),
      status: (item) => item.status?.toLowerCase() || "",
    }),
    []
  );

  const {
    sortedItems,
    sortingColumn,
    sortingDescending,
    handleSortingChange,
  } = useTableSorting({
    items,
    defaultSortingField: "completedAt",
    defaultSortingDescending: true,
    sortingComparator,
  });

  const getViewReportUrl = (item: ActivityItem): string | undefined => {
    // Generate URL based on status: Completed -> report page, others -> run page
    if (item.workflowId && item.id && item.spaceId) {
      const category = item.category?.toLowerCase();
      const basePath = `/reports-and-analysis/space/${item.spaceId}/${category}/${item.workflowId}`;
      if (item.status === ActivityStatus.COMPLETED) {
        return `${basePath}/report/${item.id}`;
      } else {
        return `${basePath}/run/${item.id}`;
      }
    }
    return undefined;
  };

  const columnDefinitions: TableProps.ColumnDefinition<ActivityItem>[] = [
    {
      id: "workflowType",
      header: <Box fontWeight="bold">Workflow Type</Box>,
      cell: (item: ActivityItem) => (
        <Badge variant="category" color="blue">
          {getWorkflowDisplayName(item.category || '')}
        </Badge>
      ),
      sortingField: "category",
      width: "12%",
      minWidth: "130px",
    },
    {
      id: "reportName",
      header: <Box fontWeight="bold">Workflow Name</Box>,
      cell: (item: ActivityItem) => (
        <Box>
          <div style={{ fontWeight: 600, marginBottom: "4px" }}>
            {item.reportName}
          </div>
          <div style={{ fontSize: "14px", color: "#5f6b7a" }}>
            {item.description}
          </div>
        </Box>
      ),
      sortingField: "reportName",
      width: "28%",
      minWidth: "240px",
    },
    {
      id: "createdAt",
      header: <Box fontWeight="bold">Execution Started</Box>,
      cell: (item: ActivityItem) => formatTimestamp(item.createdAt),
      sortingField: "createdAt",
      width: "18%",
      minWidth: "180px",
    },
    {
      id: "completedAt",
      header: <Box fontWeight="bold">Execution Completed</Box>,
      cell: (item: ActivityItem) => formatTimestamp(item.completedAt || item.date),
      sortingField: "completedAt",
      width: "18%",
      minWidth: "180px",
    },
    {
      id: "status",
      header: <Box fontWeight="bold">Status</Box>,
      cell: (item: ActivityItem) => (
        <Badge
          variant="status"
          color={
            item.status === "Completed"
              ? "green"
              : item.status === "In Progress"
              ? "blue"
              : item.status === "Timed Out"
              ? "severity-low"
              : "red"
          }
        >
          {item.status}
        </Badge>
      ),
      sortingField: "status",
      width: "12%",
      minWidth: "100px",
    },
    {
      id: "actions",
      header: <Box fontWeight="bold">Actions</Box>,
      cell: (item: ActivityItem) => {
        const viewReportUrl = getViewReportUrl(item);
        return (
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="secondary"
              href={viewReportUrl}
              disabled={!viewReportUrl}
            >
              View
            </Button>
            {showAllActions && item.downloadUrl && (
              <Button
                variant="secondary"
                icon="download"
                onClick={() => onDownload?.(item)}
              >
                Download
              </Button>
            )}
            {showAllActions && (
              <Button
                variant="secondary"
                icon="remove"
                onClick={() => onDelete?.(item)}
              >
                Delete
              </Button>
            )}
          </SpaceBetween>
        );
      },
      width: "22%",
      minWidth: "260px",
    },
  ];

  return (
    <Table
      columnDefinitions={columnDefinitions}
      items={sortedItems}
      loading={loading}
      loadingText="Loading activities..."
      sortingColumn={sortingColumn}
      sortingDescending={sortingDescending}
      onSortingChange={handleSortingChange}
      empty={
        <Box textAlign="center" color="inherit">
          <b>No activities found</b>
          <Box padding={{ bottom: "s" }} variant="p" color="inherit">
            {showAllActions 
              ? "No activities match your current filter criteria. Try adjusting your filters or check back later."
              : "No reports or analyses have been run recently."
            }
          </Box>
        </Box>
      }
      variant="borderless"
    />
  );
};

export default ActivityTable;
